





























































































































































import { Component, Vue, Watch } from 'vue-property-decorator';
import { doc, collection, getDocs, getDoc, query, orderBy } from 'firebase/firestore';
import db from '@/firebase/init';

interface LogEntry {
  type: string;
  stepNumber: number;
  stepName?: string;
  content?: any;
  metadata?: Record<string, any>;
  timestamp: any;
}

interface TaskLog {
  taskType: number;
  status: string;
  totalSteps: number;
  currentStep?: {
    number: number;
    name: string;
    status: string;
  };
  input?: Record<string, any>;
  output?: Record<string, any>;
  error?: string;
  userId: string;
  createdAt: any;
  updatedAt: any;
  completedAt?: any;
}

interface StepInfo {
  number: number;
  name: string;
  status: string;
  entries: LogEntry[];
  entryCount: number;
  startedAt: any | null;
  completedAt: any | null;
  duration: string;
}

@Component({
  name: 'AgenticTaskLogViewer'
})
export default class AgenticTaskLogViewer extends Vue {
  dialog = false;
  loading = false;
  error: string | null = null;
  taskLog: TaskLog | null = null;
  entries: LogEntry[] = [];
  expandedSections: Record<string, boolean> = {
    input: true,
    output: true
  };
  expandedSteps: Record<number, boolean> = {};

  private agencyId = '';
  private taskId = '';

  get statusColor(): string {
    switch (this.taskLog?.status) {
      case 'completed': return 'success';
      case 'error': return 'error';
      case 'processing': return 'primary';
      case 'pending': return 'warning';
      default: return 'grey';
    }
  }

  get statusText(): string {
    return this.taskLog?.status || 'Unknown';
  }

  get currentStepNumber(): number {
    return this.taskLog?.currentStep?.number || 0;
  }

  get totalStepsCount(): number {
    return this.taskLog?.totalSteps || 0;
  }

  get stepsList(): StepInfo[] {
    const steps: StepInfo[] = [];
    const totalSteps = this.taskLog?.totalSteps || 6;

    // Build step info from entries
    const stepEntries: Record<number, LogEntry[]> = {};
    const stepNames: Record<number, string> = {};
    const stepStatuses: Record<number, string> = {};
    const stepStartTimes: Record<number, any> = {};
    const stepEndTimes: Record<number, any> = {};

    for (const entry of this.entries) {
      const stepNum = entry.stepNumber;
      if (!stepEntries[stepNum]) {
        stepEntries[stepNum] = [];
      }

      if (entry.type === 'step_started') {
        stepNames[stepNum] = entry.stepName || `Step ${stepNum}`;
        stepStatuses[stepNum] = 'in_progress';
        stepStartTimes[stepNum] = entry.timestamp;
      } else if (entry.type === 'step_completed') {
        stepStatuses[stepNum] = 'completed';
        stepEndTimes[stepNum] = entry.timestamp;
      } else if (entry.type === 'error') {
        stepStatuses[stepNum] = 'error';
        stepEndTimes[stepNum] = entry.timestamp;
      }

      // Add non-step-control entries
      if (entry.type !== 'step_started' && entry.type !== 'step_completed') {
        stepEntries[stepNum].push(entry);
      }
    }

    // Override with current step from main doc
    if (this.taskLog?.currentStep) {
      const currentNum = this.taskLog.currentStep.number;
      stepNames[currentNum] = this.taskLog.currentStep.name;
      stepStatuses[currentNum] = this.taskLog.currentStep.status;
    }

    // Build step list
    for (let i = 1; i <= totalSteps; i++) {
      const entries = stepEntries[i] || [];
      const startedAt = stepStartTimes[i] || null;
      const completedAt = stepEndTimes[i] || null;
      const duration = this.calculateDuration(startedAt, completedAt);

      steps.push({
        number: i,
        name: stepNames[i] || `Step ${i}`,
        status: stepStatuses[i] || 'pending',
        entries: entries,
        entryCount: entries.length,
        startedAt,
        completedAt,
        duration
      });
    }

    return steps;
  }

  calculateDuration(start: any, end: any): string {
    if (!start || !end) return '';
    try {
      const startDate = start.toDate ? start.toDate() : new Date(start);
      const endDate = end.toDate ? end.toDate() : new Date(end);
      const diffMs = endDate.getTime() - startDate.getTime();

      if (diffMs < 1000) {
        return `${diffMs}ms`;
      } else if (diffMs < 60000) {
        const seconds = (diffMs / 1000).toFixed(1);
        return `${seconds}s`;
      } else {
        const minutes = Math.floor(diffMs / 60000);
        const seconds = Math.floor((diffMs % 60000) / 1000);
        return `${minutes}m ${seconds}s`;
      }
    } catch {
      return '';
    }
  }

  open(agencyId: string, taskId: string) {
    this.agencyId = agencyId;
    this.taskId = taskId;
    this.dialog = true;
    this.loadTaskLog();
  }

  async loadTaskLog() {
    this.loading = true;
    this.error = null;
    this.taskLog = null;
    this.entries = [];
    this.expandedSteps = {};

    try {
      // Load main document
      const docRef = doc(db, `tenants/${this.agencyId}/agenticTaskLogs/${this.taskId}`);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        this.error = 'Task log not found in Firestore';
        return;
      }

      this.taskLog = docSnap.data() as TaskLog;

      // Load entries
      const entriesRef = collection(db, `tenants/${this.agencyId}/agenticTaskLogs/${this.taskId}/entries`);
      const entriesQuery = query(entriesRef, orderBy('timestamp', 'asc'));
      const entriesSnap = await getDocs(entriesQuery);

      this.entries = entriesSnap.docs.map(d => d.data() as LogEntry);

      // Auto-expand steps with thinking entries
      for (const entry of this.entries) {
        if (entry.type === 'thinking') {
          this.$set(this.expandedSteps, entry.stepNumber, true);
        }
      }

    } catch (err: any) {
      console.error('Failed to load task log:', err);
      this.error = err.message || 'Failed to load task log';
    } finally {
      this.loading = false;
    }
  }

  toggleStep(stepNumber: number) {
    this.$set(this.expandedSteps, stepNumber, !this.expandedSteps[stepNumber]);
  }

  getStepClass(step: StepInfo): Record<string, boolean> {
    return {
      'step-completed': step.status === 'completed',
      'step-in-progress': step.status === 'in_progress',
      'step-error': step.status === 'error',
      'step-pending': step.status === 'pending'
    };
  }

  getEntryIcon(type: string): string {
    switch (type) {
      case 'thinking': return 'mdi-head-cog';
      case 'log': return 'mdi-text';
      case 'output': return 'mdi-export';
      case 'error': return 'mdi-alert';
      default: return 'mdi-circle-small';
    }
  }

  formatJson(obj: any): string {
    if (!obj) return '';
    try {
      return JSON.stringify(obj, null, 2);
    } catch {
      return String(obj);
    }
  }

  formatTime(timestamp: any): string {
    if (!timestamp) return '';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleTimeString();
    } catch {
      return '';
    }
  }

  @Watch('dialog')
  onDialogChange(val: boolean) {
    if (!val) {
      this.taskLog = null;
      this.entries = [];
      this.error = null;
    }
  }
}
